import { apiRequest } from './apiClient.js';
import type {
  ConversationDetail,
  ConversationMessagePage,
  ConversationSummary,
  MessagePagination,
} from '../types/conversation.js';
import { coalesceRequest } from './requestCoalescer.js';
import {
  INITIAL_CONVERSATION_MESSAGE_LIMIT,
  readConversationDetailCache,
  readConversationListCache,
  writeConversationDetailCache,
  writeConversationListCache,
} from './conversationSessionCache.js';

export type {
  ConversationDetail,
  ConversationMessage,
  ConversationSummary,
} from '../types/conversation.js';

export { INITIAL_CONVERSATION_MESSAGE_LIMIT };

type RawConversationDetail = ConversationDetail & {
  conversation?: {
    id: string;
    title?: string | null;
    metadata?: Record<string, unknown>;
  };
  messages?: ConversationDetail['messages'];
  pagination?: MessagePagination;
};

type GetConversationOptions = {
  messageLimit?: number;
};

function normalizeConversationDetail(raw: RawConversationDetail): ConversationDetail {
  if (raw.conversation) {
    return {
      id: raw.conversation.id,
      title: raw.conversation.title ?? raw.title ?? 'New Conversation',
      metadata: raw.conversation.metadata ?? raw.metadata,
      messages: raw.messages ?? [],
      pagination: raw.pagination,
    };
  }
  return {
    id: raw.id,
    title: raw.title ?? 'New Conversation',
    metadata: raw.metadata,
    messages: raw.messages ?? [],
    pagination: raw.pagination,
  };
}

function conversationRequestPath(conversationId: string, options?: GetConversationOptions): string {
  const params = new URLSearchParams();
  if (options?.messageLimit != null) {
    params.set('messageLimit', String(options.messageLimit));
  }
  const qs = params.toString();
  return `/api/conversations/${conversationId}${qs ? `?${qs}` : ''}`;
}

export function listConversations(): Promise<ConversationSummary[]> {
  const cached = readConversationListCache();
  const fresh = coalesceRequest('conversations:list', () =>
    apiRequest<ConversationSummary[]>('/api/conversations').then((data) => {
      writeConversationListCache(data);
      return data;
    })
  );

  if (cached && cached.length > 0) {
    void fresh.catch(() => undefined);
    return Promise.resolve(cached);
  }

  return fresh;
}

/**
 * Fetch a conversation's detail.
 *
 * Omitting `messageLimit` means "the WHOLE history" and is honoured as such
 * (#370). That matters because the session cache is keyed on conversation id
 * alone: opening a conversation caches a 50-message tail, and export — which
 * asks for everything — would otherwise be served that tail and write out a
 * silently truncated file. So a request for the whole history refuses a cache
 * entry that is not marked complete, and pays for the unbounded fetch.
 */
export async function getConversation(
  conversationId: string,
  options?: GetConversationOptions
): Promise<ConversationDetail> {
  const wantsCompleteHistory = options?.messageLimit == null;
  const cacheKey = `conversations:get:${conversationId}:${options?.messageLimit ?? 'all'}`;
  const cached = readConversationDetailCache(conversationId);

  const fetchFresh = coalesceRequest(cacheKey, async () => {
    const raw = await apiRequest<RawConversationDetail>(
      conversationRequestPath(conversationId, options)
    );
    const detail = normalizeConversationDetail(raw);
    // A bounded fetch still returns the complete history when the conversation
    // is shorter than the bound, and the server says so via `pagination`.
    // Absent pagination on a bounded fetch means an older backend that cannot
    // tell us — assume incomplete rather than cache a possible truncation.
    writeConversationDetailCache(conversationId, detail, {
      isComplete: wantsCompleteHistory || detail.pagination?.hasMore === false,
    });
    return detail;
  });

  const cacheIsUsable =
    cached !== null &&
    cached.detail.id === conversationId &&
    (!wantsCompleteHistory || cached.isComplete);

  if (cacheIsUsable) {
    // The cached detail is returned verbatim, INCLUDING its `pagination` (#370).
    //
    // It used to be re-truncated here with `messages.slice(-messageLimit)`. That
    // is wrong once pagination exists: the slice threw away messages while the
    // pagination it was paired with still described the un-sliced set, so the
    // transcript could hide history and simultaneously report that there was
    // none to load. The cache is written from an already-bounded fetch, so
    // there is nothing to re-bound; and any older, larger entry is the user's
    // own history, which is safe (if briefly heavier) to render in full.
    void fetchFresh.catch(() => undefined);
    return cached.detail;
  }

  return fetchFresh;
}

/**
 * Fetch one page of OLDER messages for a conversation — the "load earlier"
 * request (#370).
 *
 * `before` is the opaque `pagination.nextCursor` from the previous response.
 * Omitting it asks for the newest page. Deliberately NOT coalesced or cached:
 * each call is a distinct position in the history, and a stale answer here
 * would silently drop a chunk of the transcript.
 */
export function getConversationMessagePage(
  conversationId: string,
  options: { before?: string | null; limit?: number } = {}
): Promise<ConversationMessagePage> {
  const params = new URLSearchParams();
  if (options.before) {
    params.set('before', options.before);
  }
  if (options.limit != null) {
    params.set('limit', String(options.limit));
  }
  const qs = params.toString();
  return apiRequest<ConversationMessagePage>(
    `/api/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`
  );
}

export function createConversation(): Promise<ConversationSummary> {
  return apiRequest<ConversationSummary>('/api/conversations', {
    method: 'POST',
    body: {},
  });
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await apiRequest(`/api/conversations/${conversationId}`, { method: 'DELETE' });
}

export function updateConversationTitle(
  conversationId: string,
  title: string
): Promise<ConversationSummary> {
  return apiRequest<ConversationSummary>(`/api/conversations/${conversationId}`, {
    method: 'PUT',
    body: { title },
  });
}

export function togglePin(conversationId: string): Promise<ConversationSummary> {
  return apiRequest<ConversationSummary>(`/api/conversations/${conversationId}/pin`, {
    method: 'POST',
    body: {},
  });
}

export async function archiveConversation(conversationId: string): Promise<void> {
  await apiRequest(`/api/conversations/${conversationId}/archive`, {
    method: 'POST',
    body: {},
  });
}

export async function getConversationCount(): Promise<number> {
  const conversations = await listConversations();
  return conversations.length;
}

export async function loadMessages(conversationId: string): Promise<ConversationDetail['messages']> {
  const detail = await getConversation(conversationId);
  return detail.messages || [];
}
